import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { BordOneToneIcon, NudosSearchBar } from 'components/NudosComponents';
import CustomSelect from 'components/customSelect';
import useSWRInfinite from 'swr/infinite';
import useDebounce from 'hooks/useDebounce';
import useFiltersState from 'state/useFiltersState';
import { ORDER_PROPERTIES_TO_SEARCH, ORDER_STATUS_ID_CANCELLED } from 'types/orders';
import { ImoduleFilter } from 'types/global';
import OrderCancelledCard from '../../../../../components/CardInOrderList/OrderCancelledCard/OrderCancelledCard';
import { nudosGray100 } from 'styles/styles.constants';
import { getShipmentOrderFinancesCancelled } from 'services/orders';
import LoaderTrelloCard from 'views/shopping/components/LoaderTrelloCard/LoaderTrelloCard';
import UnsuccessfulSearch from 'components/unsuccessfulSearch/UnsuccessfulSearch';
import { optionsParamsSwr } from 'utils/optionsParamsSwr';
import { useGetSWRInfiniteKey } from 'utils/swrInfinite.utils';
import {
  SEARCH_TYPE_TO_FILTER_CONFIG,
  formatOrderFinances,
  getUniqueOrderFinancesByOrderId
} from '../../../trackingOrder.utils';
import { processQueryParams } from 'utils/ordersQueryParams.utils';
import './OrdersCancelledModalContent.scss';

/**
 * Contenido del modal de órdenes canceladas.
 * Flujo de queryParams: solo padre → modal. Los queryParams de OrdersListingFinances (parentQueryParams)
 * influyen en la búsqueda cuando no hay texto local. La búsqueda local (localSearchText) solo afecta
 * al modal y no debe modificar el estado ni la URL del padre.
 */
const OrdersCancelledModalContent = ({ handleClose, parentQueryParams = '' }: IOrdersCancelledModalContentProps) => {
  const {
    ordersSearchbarText,
    setOrdersSearchbarText,
    propertyToFilterSearch: globalPropertyToFilterSearch
  } = useFiltersState();
  const [localSearchText, setLocalSearchText] = useState('');
  const [localPropertyToFilterSearch, setLocalPropertyToFilterSearch] = useState<{
    name: ORDER_PROPERTIES_TO_SEARCH;
    propertyToFilter: string;
  } | null>(null);
  const containerListRef = useRef<HTMLDivElement>(null);

  const debounceDuration = 1500;
  const debouncedGlobalSearchText = useDebounce(ordersSearchbarText, debounceDuration);
  const debouncedLocalSearchText = useDebounce(localSearchText, debounceDuration);

  const effectiveSearchText = debouncedLocalSearchText || debouncedGlobalSearchText || '';
  const effectivePropertyToFilterSearch = localPropertyToFilterSearch ?? globalPropertyToFilterSearch;

  const searchTypeKey = effectivePropertyToFilterSearch?.propertyToFilter;

  const orderPropertiesToSearchValues = Object.values(ORDER_PROPERTIES_TO_SEARCH);
  const orderPropertiesToSearchKeys = Object.keys(ORDER_PROPERTIES_TO_SEARCH);

  const handleSetPropertyToFilterSearch = (value: ORDER_PROPERTIES_TO_SEARCH) =>
    setLocalPropertyToFilterSearch({
      name: value,
      propertyToFilter:
        orderPropertiesToSearchKeys?.find(
          key => ORDER_PROPERTIES_TO_SEARCH[key as keyof typeof ORDER_PROPERTIES_TO_SEARCH] === value
        ) || ''
    });

  const filtersQueryParamString = useMemo(() => {
    const filteredFilters: ImoduleFilter[] = [];

    if (effectiveSearchText && searchTypeKey && SEARCH_TYPE_TO_FILTER_CONFIG[searchTypeKey]) {
      const filterConfig = SEARCH_TYPE_TO_FILTER_CONFIG[searchTypeKey];
      const searchFilter: ImoduleFilter = {
        name: filterConfig.name,
        includes: filterConfig.includes,
        values: [effectiveSearchText.trim()]
      };
      filteredFilters.push(searchFilter);
    }

    return filteredFilters?.length > 0 ? JSON.stringify(filteredFilters) : '';
  }, [effectiveSearchText, searchTypeKey]);

  const filtersQueryParam = useMemo(() => {
    return filtersQueryParamString ? `filters=${filtersQueryParamString}` : '';
  }, [filtersQueryParamString]);

  const debouncedFiltersQuery = useDebounce(filtersQueryParam, debounceDuration);

  const prevQueryParamsRef = useRef<string>('');
  const queryParams = useMemo(() => {
    if (!debouncedFiltersQuery || debouncedFiltersQuery?.length === 0) {
      const result = '';
      if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
      prevQueryParamsRef.current = result;
      return result;
    }

    const result = `?${debouncedFiltersQuery}`;
    if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
    prevQueryParamsRef.current = result;
    return result;
  }, [debouncedFiltersQuery]);

  const effectiveQueryParams = localSearchText.trim() === '' ? parentQueryParams : queryParams;

  const swrKeyCancelled = useMemo(
    () => ['orders-cancelled-modal', ORDER_STATUS_ID_CANCELLED, effectiveQueryParams],
    [effectiveQueryParams]
  );

  const getCancelledOrdersKey = useGetSWRInfiniteKey(swrKeyCancelled);

  const {
    data: cancelledOrdersPages = [],
    isLoading,
    isValidating: isValidatingCancelled,
    setSize: setCancelledSize,
    mutate: mutateCancelledOrders
  } = useSWRInfinite(
    getCancelledOrdersKey,
    (key: (string | number)[]) => {
      const pageIndex = key?.[key?.length - 1] as number;
      const processedParams = processQueryParams(effectiveQueryParams);
      return getShipmentOrderFinancesCancelled({
        page: pageIndex + 1,
        size: 10,
        additionalQueryParams: processedParams
      });
    },
    {
      ...optionsParamsSwr({
        errorRetryCount: 0,
        suspense: false
      }),
      revalidateFirstPage: false,
      revalidateOnFocus: false
    }
  );

  const cancelledOrders = useMemo(() => {
    return getUniqueOrderFinancesByOrderId(cancelledOrdersPages.flat());
  }, [cancelledOrdersPages]);

  const formattedCancelledOrders = useMemo(() => {
    return formatOrderFinances(cancelledOrders);
  }, [cancelledOrders]);

  const handleLoadMoreCancelled = useCallback(() => {
    if (isValidatingCancelled || isLoading) return;
    const lastPage = cancelledOrdersPages?.[cancelledOrdersPages?.length - 1];
    if (lastPage && lastPage?.length === 10) {
      setCancelledSize(prev => prev + 1);
    }
  }, [isValidatingCancelled, isLoading, cancelledOrdersPages, setCancelledSize]);

  const handleRefreshData = useCallback(async (): Promise<void> => {
    if (!mutateCancelledOrders) return;
    await mutateCancelledOrders();
  }, [mutateCancelledOrders]);

  const hasMoreCancelled = useMemo(() => {
    if (!cancelledOrdersPages?.length) return false;
    const lastPage = cancelledOrdersPages?.[cancelledOrdersPages?.length - 1];
    return lastPage && lastPage?.length === 10;
  }, [cancelledOrdersPages]);

  const handleScroll = useCallback(() => {
    if (!containerListRef.current || !hasMoreCancelled || isValidatingCancelled) return;
    const { scrollTop, scrollHeight, clientHeight } = containerListRef.current;
    const threshold = 100;
    const isNearBottom = (scrollHeight ?? 0) - (scrollTop ?? 0) - (clientHeight ?? 0) < threshold;
    if (isNearBottom) {
      handleLoadMoreCancelled();
    }
  }, [hasMoreCancelled, isValidatingCancelled, handleLoadMoreCancelled]);

  const setupScrollListeners = useCallback(() => {
    const contentElement = containerListRef.current;
    if (!contentElement) return;

    contentElement.addEventListener('scroll', handleScroll);
    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const getSearchbarPlaceholder = () => {
    switch (effectivePropertyToFilterSearch?.name) {
      case ORDER_PROPERTIES_TO_SEARCH.ORGANIZATION_NAME:
        return 'Escribe el nombre de empresa';
      case ORDER_PROPERTIES_TO_SEARCH.SERIAL_NUMBER:
        return 'Escribe el número del serial';
      case ORDER_PROPERTIES_TO_SEARCH.SKU_NUMBER:
        return 'Escribe el número de SKU';
      case ORDER_PROPERTIES_TO_SEARCH.EMPLOYEE_NAME:
        return 'Escribe el nombre del empleado';
      case ORDER_PROPERTIES_TO_SEARCH.PURCHASE_ORDER:
        return 'Escribe el número de la orden de compra (PO)';
      default:
        return 'Escribe el número de orden';
    }
  };

  const updateSearchbarText = (text: string) => {
    setLocalSearchText(text);
    setOrdersSearchbarText(text);
  };

  useEffect(() => {
    return setupScrollListeners();
  }, [setupScrollListeners]);

  useEffect(() => {
    mutateCancelledOrders();
  }, [effectiveQueryParams, mutateCancelledOrders]);

  return (
    <div className="ordersCancelledModalContent">
      <div className="header">
        <div className="headerLeft">
          <div className="headerTitle">Órdenes canceladas</div>
        </div>
        <div className="headerRight">
          <div className="headerClose" onClick={handleClose}>
            <BordOneToneIcon variant="x" standardSize={18} />
          </div>
        </div>
      </div>
      <div className="containerContent">
        <div className="containerSearchBar">
          <NudosSearchBar
            customClassName="searchBarOrdersCancelled"
            customInputClass="searchBarOrdersCancelledInput"
            placeholder={getSearchbarPlaceholder()}
            handleChange={text => updateSearchbarText(text)}
            defaultValue={(localSearchText || ordersSearchbarText) ?? ''}
          />
          <CustomSelect
            customClassName="searchBarOrdersCancelledSelect"
            containerOptionsClassName="containerOptionsOrdersCancelledSelect"
            dropdownLabel="Tipo de búsqueda"
            items={orderPropertiesToSearchValues}
            value={effectivePropertyToFilterSearch?.name}
            onChangeSelect={value => handleSetPropertyToFilterSearch(value as ORDER_PROPERTIES_TO_SEARCH)}
            customBackground={nudosGray100}
            customBackgroundEmpty={nudosGray100}
          />
        </div>
        <div
          ref={containerListRef}
          className={`containerOrdersCancelledList ${formattedCancelledOrders?.length === 0 ? 'withOutPadding' : ''}`}
          onScroll={handleScroll}
        >
          {isLoading ? (
            Array.from({ length: 10 }).map((_, index) => <LoaderTrelloCard key={`loader-cancelled-${index}`} />)
          ) : formattedCancelledOrders?.length === 0 ? (
            <UnsuccessfulSearch
              iconSize={92}
              customClassName="unsuccessfulSearchOrdersCancelled"
              customTextClassName="unsuccessfulSearchOrdersCancelledText"
              customText={`No hay resultados para tu búsqueda, revisa el ${effectivePropertyToFilterSearch?.name?.toLocaleLowerCase()} o prueba con otro tipo de búsqueda`}
            />
          ) : (
            formattedCancelledOrders?.map((order, index) => (
              <OrderCancelledCard
                key={`order-cancelled-${order.orderId}-${index}`}
                data={order}
                refreshData={handleRefreshData}
              />
            ))
          )}
          {isValidatingCancelled && <LoaderTrelloCard key="loader-more-cancelled" />}
        </div>
      </div>
    </div>
  );
};

export interface IOrdersCancelledModalContentProps {
  handleClose: () => void;
  parentQueryParams?: string;
}

export default OrdersCancelledModalContent;
